// src/pages/FormsList.js
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

export default function FormsList() {
  const nav = useNavigate();
  const { user, can } = useAuth();
  const [forms, setForms] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [depts, setDepts] = useState([]);
  const [filterDept, setFilterDept] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit:20, search, dept_code: filterDept });
    const { data } = await api.get(`/forms?${params}`);
    setForms(data.data);
    setTotal(data.total);
    setLoading(false);
  }, [page, search, filterDept]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/departments').then(r => setDepts(r.data)); }, []);

  const deleteForm = async (id) => {
    if (!window.confirm('دڵنیایت لە سڕینەوە؟')) return;
    await api.delete(`/forms/${id}`);
    toast.success('سڕایەوە');
    load();
  };

  const printPDF = async (id, style) => {
    const { data } = await api.get(`/export/pdf-html/${id}?style=${style}`);
    const win = window.open('', '_blank');
    win.document.write(data.html);
    win.document.close();
    win.onload = () => win.print();
  };

  const pages = Math.ceil(total / 20);

  return (
    <div style={styles.page}>
      <div style={styles.topbar}>
        <h1 style={styles.title}>📋 فۆرمەکان ({total})</h1>
        {can('create') && (
          <button onClick={() => nav('/forms/new')} style={styles.newBtn}>➕ فۆرمی نوێ</button>
        )}
      </div>

      {/* Filters */}
      <div style={styles.filters}>
        <input style={styles.searchInput} value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="🔍 گەڕان... (ناو، UPN، ژمارەی فۆرم)" />
        {user?.role === 'admin' && (
          <select style={styles.select} value={filterDept} onChange={e => { setFilterDept(e.target.value); setPage(1); }}>
            <option value="">هەموو بەشەکان</option>
            {depts.map(d => <option key={d.id} value={d.code}>{d.name_ku}</option>)}
          </select>
        )}
      </div>

      <div style={styles.tableWrap}>
        {loading ? (
          <div style={{textAlign:'center',padding:40,color:'#888'}}>چاوەڕوانبە...</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr style={styles.thead}>
                <th style={styles.th}>ژمارەی فۆرم</th>
                <th style={styles.th}>ناوی فەرمانبەر</th>
                <th style={styles.th}>UPN</th>
                <th style={styles.th}>بەش</th>
                <th style={styles.th}>منداڵ</th>
                <th style={styles.th}>بەروار</th>
                <th style={styles.th}>کردارەکان</th>
              </tr>
            </thead>
            <tbody>
              {forms.map(f => (
                <tr key={f.id} style={styles.tr}>
                  <td style={styles.td}><span style={styles.badge}>{f.form_number}</span></td>
                  <td style={styles.td}>{f.employee_name}</td>
                  <td style={styles.td}><code style={{fontSize:11}}>{f.upn}</code></td>
                  <td style={styles.td}><span style={styles.deptBadge}>{f.dept_name}</span></td>
                  <td style={styles.td}>{f.children_count}</td>
                  <td style={styles.td}>{new Date(f.created_at).toLocaleDateString('ku')}</td>
                  <td style={{...styles.td, whiteSpace:'nowrap'}}>
                    <button onClick={() => nav(`/forms/${f.id}`)} style={styles.btn}>✏️</button>
                    <button onClick={() => printPDF(f.id,'classic')} style={styles.btn} title="پرینتی کلاسیک">🖨️</button>
                    <button onClick={() => printPDF(f.id,'modern')} style={styles.btn} title="پرینتی مۆدێرن">🎨</button>
                    <button onClick={() => window.open(`/api/export/excel/${f.id}`,'_blank')} style={styles.btn} title="ئێکزڵ">📊</button>
                    {can('delete') && <button onClick={() => deleteForm(f.id)} style={styles.delBtn}>🗑️</button>}
                  </td>
                </tr>
              ))}
              {forms.length === 0 && (
                <tr><td colSpan={7} style={{textAlign:'center',padding:30,color:'#999'}}>هیچ فۆرمێک نەدۆزرایەوە</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={styles.pagination}>
          <button disabled={page===1} onClick={() => setPage(p=>p-1)} style={styles.pageBtn}>‹ پێشوو</button>
          <span style={{fontSize:13,color:'#555'}}>لاپەڕە {page} لە {pages}</span>
          <button disabled={page===pages} onClick={() => setPage(p=>p+1)} style={styles.pageBtn}>دواتر ›</button>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { padding:20 },
  topbar: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 },
  title: { fontSize:20, fontWeight:'bold', color:'#1a5276', margin:0 },
  newBtn: { background:'linear-gradient(135deg,#1a5276,#2980b9)', color:'#fff', border:'none', borderRadius:8, padding:'10px 18px', fontSize:13, fontWeight:'bold', cursor:'pointer' },
  filters: { display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' },
  searchInput: { flex:1, minWidth:200, padding:'9px 12px', border:'1.5px solid #ddd', borderRadius:8, fontSize:13, outline:'none', fontFamily:'inherit' },
  select: { padding:'9px 12px', border:'1.5px solid #ddd', borderRadius:8, fontSize:13, fontFamily:'inherit' },
  tableWrap: { background:'#fff', borderRadius:10, boxShadow:'0 2px 8px rgba(0,0,0,0.07)', overflow:'hidden' },
  table: { width:'100%', borderCollapse:'collapse' },
  thead: { background:'#1a5276' },
  th: { color:'#fff', padding:'11px 12px', textAlign:'right', fontSize:12, fontWeight:'bold' },
  tr: { borderBottom:'1px solid #f0f0f0', transition:'background 0.1s' },
  td: { padding:'10px 12px', fontSize:13, color:'#333' },
  badge: { background:'#eaf2fb', color:'#1a5276', borderRadius:4, padding:'2px 8px', fontSize:11, fontWeight:'bold', whiteSpace:'nowrap' },
  deptBadge: { background:'#eafaf1', color:'#1e8449', borderRadius:4, padding:'2px 8px', fontSize:11 },
  btn: { background:'#f0f4f8', border:'none', borderRadius:4, padding:'4px 8px', cursor:'pointer', marginLeft:3, fontSize:14 },
  delBtn: { background:'#fdecea', border:'none', borderRadius:4, padding:'4px 8px', cursor:'pointer', marginLeft:3, fontSize:14 },
  pagination: { display:'flex', justifyContent:'center', alignItems:'center', gap:12, marginTop:16 },
  pageBtn: { background:'#fff', border:'1px solid #ddd', borderRadius:6, padding:'6px 14px', cursor:'pointer', fontSize:13 },
};
